/* eslint-disable react/jsx-props-no-spreading */

import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

import {
  Checkbox,
  Container,
  ContainerCheckbox,
  GridTheme,
  MenuItem,
  MenuTab,
  MenuTextContainer,
  SideMenu,
  TabHeader,
  TableContainer,
  TextTabHeader,
} from './styles';

ModuleRegistry.registerModules([AllCommunityModule]);

const FilterableGrid = (props) => {
  const {
    defaultColumns,
    keyFilters,
    preSetFilters,
    onSetSelection,
    onHandleApiCall,
    defaultFilters,
  } = props;
  const gridRef = useRef();

  const [gridApi, setGridApi] = useState(null);
  const [activeSidebar, setActiveSideBar] = useState('');
  const [rows, setRows] = useState(null);
  const [columns, setColumns] = useState(defaultColumns);
  const [data, setData] = useState('');

  useEffect(() => {
    const newRows = _.filter(rows, (row) => {
      let show = true;
      _.forEach(keyFilters, (value, filterName) => {
        if (!_.isEmpty(keyFilters[filterName]) && !_.includes(_.lowerCase(row[filterName]), _.lowerCase(keyFilters[filterName]))) {
          show = false;
        }
      });

      return show;
    });

    setData(!_.isEmpty(newRows) ? newRows : null);
  }, [rows]);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.api.setFilterModel(preSetFilters);
  }, [preSetFilters]);

  const handleSelectAll = (event) => {
    const { api } = gridRef.current;
    const count = api.getDisplayedRowCount();
    if (event.target.checked) {
      for (let i = 0; i < count; i += 1) {
        api.getDisplayedRowAtIndex(i).setSelected(true, false, true);
      }
    } else {
      for (let i = 0; i < count; i += 1) {
        api.getDisplayedRowAtIndex(i).setSelected(false, false, true);
      }
    }
    api.refreshCells();
    const selectedRows = api.getSelectedRows();
    onSetSelection(selectedRows.map((row) => (row.id)));
  };

  useEffect(() => {
    const selectAll = document.querySelector('.ag-header-select-all');
    if (selectAll) {
      selectAll.classList.remove('ag-hidden');
      selectAll.addEventListener('click', (event) => {
        handleSelectAll(event);
      });
    }
    return () => {
      if (selectAll) {
        selectAll.classList.add('ag-hidden');
        selectAll.removeEventListener('click', (event) => {
          handleSelectAll(event);
        });
      }
    };
  });

  const handleChangeColumns = (field) => {
    let clone = _.cloneDeep(columns);
    clone = _.map(clone, (column) => ({
      ...column,
      ...(column.field === field && { hide: !column.hide }),
    }));

    setColumns(clone);
  };

  const handleGridReady = async (params) => {
    const api = params?.api || params;

    const dataSource = {
      getRows: async (rowsParams) => {
        const {
          startRow,
          endRow,
          successCallback,
          failCallback,
          sortModel,
          filterModel,
        } = rowsParams;

        const filtersKeyMap = {
          firstName: 'first_name',
          lastName: 'last_name',
          language: 'languages',
          ethnicity: 'race_ethnic_heritage',
          householdIncome: 'annual_household_income',
          companySize: ['company', 'size'],
          seniority: ['current_position', 'seniority'],
          currentWorkStatus: 'work_status',
          industry: ['company', 'industry'],
          jobRole: 'job_categories',
          age: 'birthdate',
          projectName: 'projects.name',
          gender: 'gender',
        };
        let { order } = props;
        let tableFilter = '';
        if (!_.isEmpty(sortModel)) {
          const [{ colId, sort }] = sortModel;
          const key = filtersKeyMap[colId] || colId;
          order = `${key} ${sort}`;
        }
        if (!_.isEmpty(filterModel)) {
          const keys = _.keys(filterModel);
          _.forEach(keys, (key) => {
            const { type, value } = filterModel[key];
            const searchKey = filtersKeyMap[key] || key;

            const stringValue = (type === 'text') ? JSON.stringify(value[0]) : JSON.stringify(value);

            tableFilter += `${JSON.stringify(searchKey)}::${stringValue || ''}::${type}//`;
          });
        }
        const limit = endRow - startRow;
        const apiCallParams = {
          limit,
          offset: startRow,
          order,
          tableFilter,
          ...defaultFilters,
        };

        try {
          const { records, totalRecords } = await onHandleApiCall(apiCallParams);
          if (totalRecords > 50) {
            successCallback(records, totalRecords);
          } else {
            successCallback(records, records.length);
          }
          const selectAll = document.querySelector('.ag-header-select-all').children[1];
          const checkBox = document.querySelector('.ag-header-select-all').children[1].children[0];
          checkBox.checked = false;
          selectAll.classList.remove('ag-checked');
          onSetSelection([]);
          setRows(records);
        } catch (error) {
          failCallback(error);
        }
      },
    };
    api.setGridOption('datasource', dataSource);
    setGridApi(params);
  };

  const handleSelect = () => {
    const { api } = gridRef.current;
    const selectedRows = api.getSelectedRows();
    onSetSelection(selectedRows.map((row) => (row.id)));
  };

  const handleActiveSideBar = (item) => {
    setActiveSideBar(item === activeSidebar ? '' : item);
  };

  useEffect(() => {
    if (gridApi) {
      handleGridReady(gridApi);
    }
  }, [defaultFilters]);

  const handleForceRefresh = () => {
    if (gridApi) {
      handleGridReady(gridApi);
    }
    // Set the state to trigger a refresh when the button or event occurs.
  };
  return (
    <Container>
      <TableContainer>
        <AgGridReact
          theme={GridTheme}
          columnDefs={columns}
          ref={gridRef}
          onGridReady={handleGridReady}
          defaultColDef={{
            initialWidth: 100,
            sortable: false,
            resizable: true,
          }}
          pagination
          rowModelType="infinite"
          cacheBlockSize={50}
          paginationPageSize={50}
          rowSelection={{ mode: 'multiRow' }}
          selectionColumnDef={{
            width: 32,
            pinned: 'left',
          }}
          onSelectionChanged={handleSelect}
          rowData={data}
          handleForceRefresh={handleForceRefresh}
          context={{
            updateRow: setRows,
          }}
        />
      </TableContainer>

      {activeSidebar === 'columns' && (
        <MenuTab>
          <TabHeader>
            <TextTabHeader>
              Hide columns
            </TextTabHeader>
          </TabHeader>
          <ContainerCheckbox>
            {_.map(columns, (column) => (
              <Checkbox
                label={column.headerName}
                value={column.field}
                checked={!column.hide}
                onChange={() => handleChangeColumns(column.field)}
              />
            ))}
          </ContainerCheckbox>
        </MenuTab>
      )}
      <SideMenu>
        <MenuItem>
          <MenuTextContainer onClick={() => handleActiveSideBar('columns')}>
            Columns
          </MenuTextContainer>
        </MenuItem>
      </SideMenu>
    </Container>
  );
};

FilterableGrid.propTypes = {
  defaultColumns: PropTypes.arrayOf(PropTypes.shape({
    headerName: PropTypes.string,
    field: PropTypes.string,
  })).isRequired,
  preSetFilters: PropTypes.object.isRequired,
  keyFilters: PropTypes.object.isRequired,
  onHandleApiCall: PropTypes.func.isRequired,
  onSetSelection: PropTypes.func,
  order: PropTypes.string,
  defaultFilters: PropTypes.object,
};

FilterableGrid.defaultProps = {
  onSetSelection: () => { },
  order: undefined,
  defaultFilters: {},
};

export default FilterableGrid;
